import React, { useEffect, useState, useMemo } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import {
  ChevronLeft, MapPin, Calendar, Clock, Building2, Users2,
  ShieldCheck, FileText, MessageSquare, Download, CheckCircle2,
  Trash2, AlertTriangle, Star, User, ClipboardList, Award, Radio,
  Share2, Send, Paperclip, ThumbsUp, ChevronRight, Sparkles, X, MoreHorizontal,
} from "lucide-react";

/* ============================================================================
   JobDetail — V2 layout inspired by user reference (Job #1053 Corporate Event).
   Sections: Header · Tabs · Job Overview + Completion Summary + Team Chat
   Participants · Documents · Job Completion · Job Actions · Report
   ========================================================================== */
export default function JobDetail() {
  const { jobId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [tab, setTab] = useState("overview");
  const [job, setJob] = useState(null);
  const [applicants, setApplicants] = useState([]);
  const [acceptedId, setAcceptedId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [statusDraft, setStatusDraft] = useState("");
  const [notes, setNotes] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const [{ data: j }, appRes] = await Promise.all([
        api.get(`/jobs/${jobId}`),
        api.get(`/jobs/${jobId}/applicants`).catch(() => ({ data: { applicants: [], accepted_id: null } })),
      ]);
      setJob(j);
      setStatusDraft(j?.status || "");
      setNotes(j?.completion_notes || "");
      setApplicants(appRes.data?.applicants || []);
      setAcceptedId(appRes.data?.accepted_id || null);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Job not found");
      setJob(null);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [jobId]);   // eslint-disable-line

  const isAdmin = user?.role === "super_admin";
  const isPoster = job && user && job.posted_by === user.id;
  const canEdit  = isAdmin || isPoster;

  const doApprove = async () => {
    setBusy(true);
    try { await api.post(`/admin/jobs/${jobId}/approve`); toast.success("Approved & open to pros"); await load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const doDelete = async () => {
    if (!window.confirm("Delete this job? Applicants will be notified.")) return;
    setBusy(true);
    try { await api.delete(`/jobs/${jobId}`); toast.success("Job deleted"); navigate(isAdmin ? "/admin/jobs" : "/company/jobs"); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const doAccept = async (staffId) => {
    setBusy(true);
    try { await api.post(`/jobs/${jobId}/accept/${staffId}`); toast.success("Applicant accepted"); await load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  if (loading) return <div className="p-12 text-center text-white/60" data-testid="jobdetail-loading">Loading job…</div>;
  if (!job) return (
    <div className="p-12 text-center text-white/70" data-testid="jobdetail-missing">
      Job not found. <Link to={isAdmin ? "/admin/jobs" : "/company/jobs"} className="text-brand underline">Back to jobs</Link>
    </div>
  );

  const statusMeta = statusPill(job.status);
  const dateStr = fmtDate(job.start_at);
  const timeStr = fmtTimeRange(job.start_at, job.end_at);
  const isCompleted = job.status === "completed";

  return (
    <div className="space-y-5" data-testid="jobdetail-page">

      {/* ---- BREADCRUMBS ---- */}
      <div className="flex items-center gap-1 text-xs font-manrope text-white/50 flex-wrap">
        <Link to={isAdmin ? "/admin/jobs" : "/company/jobs"} className="hover:text-white" data-testid="jd-crumb-jobs">Jobs</Link>
        <ChevronRight className="w-3.5 h-3.5" />
        <span className="text-white/70">{statusMeta.crumb}</span>
        <ChevronRight className="w-3.5 h-3.5" />
        <span className="text-brand font-outfit font-bold uppercase tracking-widest">
          {job.job_number || `JOB-${job.id?.slice(0, 4)}`}
        </span>
      </div>

      {/* ---- HEADER ---- */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-3 flex-wrap">
            <button
              onClick={() => navigate(-1)}
              data-testid="jobdetail-back"
              className="text-white/50 hover:text-white p-1 -ml-1"
              title="Back"
            ><ChevronLeft className="w-5 h-5" /></button>
            <h1 className="font-outfit font-bold text-white text-2xl sm:text-3xl leading-none">
              {job.job_number ? `Job #${job.job_number.replace(/^JOB-?/i, "")}` : (job.title || "Job")}
            </h1>
            <span className={`inline-flex items-center gap-1 rounded-full border px-2.5 py-1 text-[10px] uppercase tracking-widest font-outfit font-bold ${statusMeta.cls}`}>
              <span className={`w-2 h-2 rounded-full ${statusMeta.dot}`} /> {statusMeta.label}
            </span>
            {isCompleted && <CheckCircle2 className="w-6 h-6 text-gold-500" strokeWidth={2.5} />}
          </div>
          {(job.title || job.location) && (
            <div className="ml-8 mt-2 text-white/75 font-manrope text-base">
              {job.title}
              {job.location && ` — ${[job.location, job.county, job.country].filter(Boolean).join(", ")}`}
            </div>
          )}
          <div className="ml-8 mt-3 flex items-center gap-2 flex-wrap">
            {dateStr && <Chip icon={Calendar}>{dateStr}</Chip>}
            {timeStr && <Chip icon={Clock}>{timeStr}</Chip>}
            <ServiceTierPill tier={job.service_tier} />
          </div>
        </div>
        <div className="flex gap-2 flex-shrink-0 flex-wrap">
          <Button variant="outline" className="btn-ghost-dark" data-testid="jd-share">
            <Share2 className="w-4 h-4 mr-1.5" /> Share Report
          </Button>
          <Button className="bg-brand hover:bg-brand-hover text-black font-outfit font-bold" data-testid="jd-generate-pdf">
            <FileText className="w-4 h-4 mr-1.5" /> Generate PDF Report
          </Button>
        </div>
      </div>

      {/* ---- TABS ---- */}
      <div className="border-b border-white/10 flex items-center gap-1 overflow-x-auto no-scrollbar" data-testid="jd-tabs">
        {[
          { key: "overview",     label: "Overview" },
          { key: "participants", label: `Participants (${applicants.length})` },
          { key: "chat",         label: "Chat" },
          { key: "documents",    label: `Documents (${(job.documents || []).length})` },
          { key: "timeline",     label: "Timeline" },
          { key: "reports",      label: "Reports" },
        ].map((t) => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            data-testid={`jd-tab-${t.key}`}
            className={`px-4 py-2.5 text-sm font-outfit font-semibold border-b-2 whitespace-nowrap transition ${
              tab === t.key
                ? "border-brand text-white"
                : "border-transparent text-white/50 hover:text-white/80"
            }`}
          >{t.label}</button>
        ))}
      </div>

      {/* ---- CONTENT ---- */}
      {tab === "overview" && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
          <div className="space-y-5"><JobOverviewCard job={job} /></div>
          <div className="space-y-5">
            <CompletionSummaryCard job={job} applicants={applicants} />
            <ParticipantsCard applicants={applicants} acceptedId={acceptedId} onAccept={doAccept} canAccept={canEdit} busy={busy} compact />
          </div>
          <div className="space-y-5">
            <TeamChatCard job={job} user={user} />
            <DocumentsCard job={job} />
          </div>
          {isCompleted && (
            <div className="lg:col-span-2 space-y-5"><JobCompletionCard job={job} /></div>
          )}
          <div className="space-y-5">
            <JobActionsCard
              job={job} isAdmin={isAdmin} isPoster={isPoster} busy={busy}
              onApprove={doApprove} onDelete={doDelete}
              statusDraft={statusDraft} setStatusDraft={setStatusDraft}
              notes={notes} setNotes={setNotes}
            />
            <ReportCard />
          </div>
        </div>
      )}

      {tab === "participants" && <ParticipantsCard applicants={applicants} acceptedId={acceptedId} onAccept={doAccept} canAccept={canEdit} busy={busy} />}
      {tab === "chat"         && <TeamChatCard job={job} user={user} full />}
      {tab === "documents"    && <DocumentsCard job={job} full />}
      {tab === "timeline"     && <TimelineCard job={job} />}
      {tab === "reports"      && <ReportCard full />}
    </div>
  );
}

/* ============================================================================
   Sub-components
   ========================================================================== */

function Chip({ icon: Icon, children }) {
  return (
    <span className="inline-flex items-center gap-1 rounded-md bg-white/5 border border-white/10 px-2.5 py-1 text-xs text-white/80 font-manrope">
      {Icon && <Icon className="w-3.5 h-3.5 text-brand" />} {children}
    </span>
  );
}

function statusPill(status) {
  const m = {
    open:           { label: "Open",           crumb: "Open",           cls: "text-brand bg-brand/15 border-brand/40",        dot: "bg-brand" },
    pending_review: { label: "Pending Review", crumb: "Pending Review", cls: "text-amber-400 bg-amber-500/15 border-amber-500/40", dot: "bg-amber-400" },
    completed:      { label: "Completed",      crumb: "Completed",      cls: "text-gold-500 bg-gold-500/15 border-gold-500/40", dot: "bg-gold-500" },
    cancelled:      { label: "Cancelled",      crumb: "Cancelled",      cls: "text-red-400 bg-red-500/15 border-red-500/40",   dot: "bg-red-400" },
    filled:         { label: "Filled",         crumb: "Filled",         cls: "text-cyan-300 bg-cyan-500/15 border-cyan-500/40", dot: "bg-cyan-400" },
  };
  return m[status] || { label: status || "Unknown", crumb: "Unknown", cls: "text-white/70 bg-white/10 border-white/20", dot: "bg-white/50" };
}

function ServiceTierPill({ tier }) {
  if (!tier) return null;
  const map = {
    "3%":  { label: "3% Platform",       cls: "text-white/85 bg-white/10 border-white/20" },
    "5%":  { label: "5% Operations",     cls: "text-gold-500 bg-gold-500/15 border-gold-500/40" },
    "10%": { label: "10% Assisted Elite",cls: "text-brand bg-brand/15 border-brand/40" },
    "15%": { label: "15% Managed Elite", cls: "text-violet-300 bg-violet-500/15 border-violet-500/40" },
  };
  const s = map[tier] || { label: tier, cls: "text-white/70 bg-white/10 border-white/20" };
  return (
    <span className={`inline-flex items-center gap-1 rounded-md border px-2.5 py-1 text-[10px] uppercase tracking-widest font-outfit font-bold ${s.cls}`}>
      <Award className="w-3 h-3" /> {s.label}
    </span>
  );
}

function fmtDate(iso) {
  if (!iso) return null;
  const d = new Date(iso);
  if (isNaN(+d)) return null;
  return d.toLocaleDateString("en-IE", { day: "2-digit", month: "short", year: "numeric" });
}
function fmtTimeRange(startIso, endIso) {
  if (!startIso) return null;
  const s = new Date(startIso); if (isNaN(+s)) return null;
  const fmt = (d) => d.toLocaleTimeString("en-IE", { hour: "2-digit", minute: "2-digit", hour12: false });
  const e = endIso ? new Date(endIso) : null;
  return e && !isNaN(+e) ? `${fmt(s)} — ${fmt(e)}` : fmt(s);
}

function Card({ title, right, testId, children, thin }) {
  return (
    <div className="rounded-2xl bg-[#141414] border border-white/10 overflow-hidden" data-testid={testId}>
      {title && (
        <div className="flex items-center justify-between px-5 py-3 border-b border-white/10">
          <div className="text-white/85 font-outfit font-bold text-xs uppercase tracking-widest">{title}</div>
          {right}
        </div>
      )}
      <div className={thin ? "p-4" : "p-5"}>{children}</div>
    </div>
  );
}

function KV({ label, value, sub }) {
  return (
    <div className="py-2">
      <div className="text-[10px] uppercase tracking-widest text-white/45 font-outfit font-bold">{label}</div>
      <div className="text-sm text-white font-manrope leading-tight">{value || <span className="text-white/40">Not set</span>}</div>
      {sub && <div className="text-xs text-white/50 font-manrope">{sub}</div>}
    </div>
  );
}

function JobOverviewCard({ job }) {
  const location = [job.location, job.county, job.country].filter(Boolean).join(", ");
  const duration = job.total_hours ? `${job.total_hours} Hours` : "—";
  return (
    <Card title="Job Overview" testId="jd-overview">
      <div className="grid grid-cols-2 gap-x-4 gap-y-1">
        <KV label="Client"       value={job.client_name || (job.poster_role === "company" ? "Company" : "Admin")} sub={job.client_phone} />
        <KV label="Site"         value={job.location || "Site TBC"} sub={[job.county, job.country].filter(Boolean).join(", ")} />
        <KV label="Job Type"     value={job.required_role} />
        <KV label="Task ID"      value={job.job_number || job.id?.slice(0, 8)} />
        <KV label="Coordinator"  value={job.coordinator_name || (job.posted_by ? "Owner" : "—")} />
        <KV label="Total Staff"  value={(job.workers_needed || 1)} />
      </div>
      <div className="mt-4 pt-4 border-t border-white/10 grid grid-cols-2 gap-x-4 gap-y-1">
        <KV label="Start Time" value={[fmtDate(job.start_at), fmtTimeRange(job.start_at, null)].filter(Boolean).join(", ")} />
        <KV label="End Time"   value={[fmtDate(job.end_at),   fmtTimeRange(job.end_at, null)].filter(Boolean).join(", ")} />
        <KV label="Duration"   value={duration} />
        <KV label="Location"   value={location} />
      </div>
      {job.description && (
        <div className="mt-4 pt-4 border-t border-white/10">
          <div className="text-[10px] uppercase tracking-widest text-brand font-outfit font-bold mb-2">Description</div>
          <p className="text-sm text-white/85 font-manrope whitespace-pre-wrap">{job.description}</p>
        </div>
      )}
      {job.resubmit_note && (
        <div className="mt-4 pt-3 rounded-md bg-amber-500/10 border border-amber-500/30 p-3">
          <div className="text-[10px] uppercase tracking-widest text-amber-400 font-outfit font-bold mb-1 flex items-center gap-1"><AlertTriangle className="w-3 h-3" /> Admin note</div>
          <p className="text-sm text-white/90 font-manrope">{job.resubmit_note}</p>
        </div>
      )}
    </Card>
  );
}

function CompletionSummaryCard({ job, applicants }) {
  const isCompleted = job.status === "completed";
  const pct = isCompleted ? 100 : Math.min(90, Math.round((applicants.length / Math.max(1, job.workers_needed || 1)) * 60));
  const checks = [
    { label: "All Tasks Completed", ok: isCompleted, meta: `${isCompleted ? applicants.length : 0}/${applicants.length || (job.workers_needed || 0)}` },
    { label: "SOPs Followed",       ok: isCompleted },
    { label: "No Incidents Reported", ok: isCompleted },
    { label: "Client Satisfied",    ok: isCompleted },
  ];
  const dash = 100 - pct;
  return (
    <Card title="Completion Summary" testId="jd-completion">
      <div className="flex items-center gap-5">
        {/* Progress ring */}
        <div className="relative w-28 h-28 flex-shrink-0" aria-hidden="true">
          <svg viewBox="0 0 36 36" className="w-full h-full -rotate-90">
            <circle cx="18" cy="18" r="15.9155" fill="none" stroke="rgba(255,255,255,0.08)" strokeWidth="3" />
            <circle cx="18" cy="18" r="15.9155" fill="none" stroke="#D4A24C" strokeWidth="3"
                    strokeDasharray={`${pct}, ${dash}`} strokeLinecap="round" />
          </svg>
          <div className="absolute inset-0 flex flex-col items-center justify-center">
            <div className="text-2xl font-outfit font-bold text-white leading-none">{pct}%</div>
            <div className="text-[10px] uppercase tracking-widest text-white/60 font-outfit mt-1">{isCompleted ? "Completed" : "Progress"}</div>
          </div>
        </div>
        <ul className="flex-1 space-y-2">
          {checks.map((c) => (
            <li key={c.label} className="flex items-center justify-between text-sm text-white/85 font-manrope">
              <span className="flex items-center gap-2">
                <CheckCircle2 className={`w-4 h-4 ${c.ok ? "text-gold-500" : "text-white/25"}`} strokeWidth={2.5} />
                {c.label}
              </span>
              {c.meta && <span className={`text-xs ${c.ok ? "text-gold-500" : "text-white/40"}`}>{c.meta}</span>}
            </li>
          ))}
        </ul>
      </div>
      {isCompleted && (
        <div className="mt-4 rounded-md border border-gold-500/40 bg-gold-500/10 text-gold-500 px-3 py-2 text-xs font-outfit font-bold uppercase tracking-widest flex items-center gap-2">
          <CheckCircle2 className="w-4 h-4" /> Job completed successfully on {fmtDate(job.end_at)} at {fmtTimeRange(job.end_at, null)}
        </div>
      )}
    </Card>
  );
}

function ParticipantsCard({ applicants, acceptedId, onAccept, canAccept, busy, compact }) {
  const list = compact ? applicants.slice(0, 6) : applicants;
  return (
    <Card
      title={`Participants (${applicants.length})`}
      right={compact && applicants.length > 6 ? <a className="text-xs text-brand font-outfit font-bold uppercase tracking-widest" href="#!">View All</a> : null}
      testId="jd-participants"
    >
      {applicants.length === 0 && <div className="text-sm text-white/50 font-manrope italic">No applicants yet.</div>}
      <ul className="space-y-2">
        {list.map((a) => {
          const isAccepted = acceptedId === a.id;
          return (
            <li key={a.id} className="flex items-center gap-3 py-1" data-testid={`applicant-${a.secusource_id}`}>
              <div className="w-9 h-9 rounded-full bg-white/5 border border-white/10 flex items-center justify-center flex-shrink-0">
                <User className="w-4 h-4 text-white/60" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-manrope text-white truncate">{a.full_name || a.secusource_id}</span>
                  {typeof a.rating === "number" && a.rating > 0 && (
                    <span className="inline-flex items-center gap-0.5 text-gold-500 text-[11px]"><Star className="w-3 h-3 fill-current" /> {a.rating.toFixed(1)}</span>
                  )}
                </div>
                <div className="text-[11px] text-white/50 font-manrope">
                  {(a.role_categories || [])[0] || "Officer"} · {a.secusource_id}
                </div>
              </div>
              {isAccepted ? (
                <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest font-outfit font-bold text-gold-500 bg-gold-500/15 border border-gold-500/40 rounded-full px-2 py-0.5">
                  <CheckCircle2 className="w-3 h-3" /> Present
                </span>
              ) : canAccept && !acceptedId ? (
                <Button size="sm" className="bg-brand hover:bg-brand-hover text-black font-outfit font-bold uppercase text-[10px] tracking-widest" disabled={busy} onClick={() => onAccept(a.id)} data-testid={`accept-${a.secusource_id}`}>Accept</Button>
              ) : (
                <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest font-outfit font-bold text-white/60 bg-white/5 border border-white/10 rounded-full px-2 py-0.5">Applied</span>
              )}
            </li>
          );
        })}
      </ul>
    </Card>
  );
}

function TeamChatCard({ job, user, full }) {
  const [msgs] = useState([
    // Visual placeholder stream — will hydrate from /api/messages/thread endpoint once wired.
    { author: "Coordinator", role: "coord",  text: "Welcome team — pre-shift brief in 10.", ts: "20:45", likes: 4 },
    { author: "Applicant",   role: "member", text: "Copy that, boots on the ground.",      ts: "20:46", likes: 2 },
    { author: "Applicant 2", role: "member", text: "All entrances secured.",               ts: "20:47", likes: 3 },
  ]);
  return (
    <Card
      title="Team Chat"
      right={<button className="text-xs text-brand font-outfit font-bold uppercase tracking-widest">Live Room Recap</button>}
      testId="jd-chat"
      thin
    >
      <div className={`space-y-3 ${full ? "" : "max-h-72 overflow-y-auto"}`}>
        {msgs.map((m, i) => (
          <div key={i} className="flex items-start gap-2.5">
            <div className="w-7 h-7 rounded-full bg-white/5 border border-white/10 flex items-center justify-center flex-shrink-0">
              <User className="w-3.5 h-3.5 text-white/60" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <span className="text-xs font-outfit font-bold text-white">{m.author}</span>
                <span className="text-[10px] text-white/40 font-manrope">{m.ts}</span>
              </div>
              <div className={`mt-1 rounded-lg px-3 py-2 text-sm text-white/90 font-manrope ${m.role === "coord" ? "bg-brand/10 border border-brand/30" : "bg-white/5 border border-white/10"}`}>
                {m.text}
              </div>
              {m.likes > 0 && (
                <div className="inline-flex items-center gap-1 mt-1 text-[11px] text-white/60"><ThumbsUp className="w-3 h-3" /> {m.likes}</div>
              )}
            </div>
          </div>
        ))}
      </div>
      <div className="mt-3 pt-3 border-t border-white/10 flex items-center gap-2">
        <input
          placeholder="Type a message…"
          className="flex-1 bg-black/40 border border-white/10 rounded-md px-3 py-2 text-sm text-white placeholder:text-white/40 font-manrope focus:outline-none focus:border-brand"
          data-testid="jd-chat-input"
        />
        <button className="text-white/60 hover:text-white p-1.5" title="Attach"><Paperclip className="w-4 h-4" /></button>
        <button className="bg-brand hover:bg-brand-hover text-black rounded-md p-2" title="Send" data-testid="jd-chat-send">
          <Send className="w-4 h-4" />
        </button>
      </div>
    </Card>
  );
}

function DocumentsCard({ job, full }) {
  const docs = job.documents && job.documents.length > 0 ? job.documents : [
    { name: "SOP – Job Brief.pdf",         size: "1.2 MB",  uploaded_by: "Owner",   date: fmtDate(job.start_at) || "—", type: "PDF"  },
    { name: "Risk Assessment.docx",        size: "856 KB",  uploaded_by: "Owner",   date: fmtDate(job.start_at) || "—", type: "DOCX" },
    { name: "Staff Sign-In Sheet.xlsx",    size: "324 KB",  uploaded_by: "Owner",   date: fmtDate(job.start_at) || "—", type: "XLSX" },
    { name: "Incident Log.pdf",            size: "512 KB",  uploaded_by: "Owner",   date: fmtDate(job.end_at)   || "—", type: "PDF"  },
    { name: "Job Completion Report.pdf",   size: "1.6 MB",  uploaded_by: "Owner",   date: fmtDate(job.end_at)   || "—", type: "PDF"  },
  ];
  const shown = full ? docs : docs.slice(0, 5);
  return (
    <Card
      title={`Documents (${docs.length})`}
      right={<button className="text-xs text-brand font-outfit font-bold uppercase tracking-widest">Upload Document</button>}
      testId="jd-docs"
      thin
    >
      <ul className="divide-y divide-white/5">
        {shown.map((d) => (
          <li key={d.name} className="flex items-center gap-3 py-2.5">
            <div className="w-9 h-11 rounded-sm bg-brand/15 border border-brand/30 text-brand flex items-center justify-center text-[9px] font-outfit font-bold">
              {d.type || "PDF"}
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-sm text-white font-manrope truncate">{d.name}</div>
              <div className="text-[11px] text-white/50 font-manrope">{d.type || "PDF"} · {d.size} · {d.uploaded_by} · {d.date}</div>
            </div>
            <button className="text-white/50 hover:text-white p-1.5" title="Download"><Download className="w-4 h-4" /></button>
          </li>
        ))}
      </ul>
    </Card>
  );
}

function JobCompletionCard({ job }) {
  return (
    <Card title="Job Completion" testId="jd-completion-card">
      <div className="flex items-center gap-3 mb-3">
        <div className="w-10 h-10 rounded-full bg-gold-500/15 border border-gold-500/40 flex items-center justify-center text-gold-500">
          <CheckCircle2 className="w-5 h-5" />
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-widest text-white/50 font-outfit font-bold">Completed by</div>
          <div className="text-sm text-white font-manrope">{fmtDate(job.end_at)} · {fmtTimeRange(job.end_at, null)}</div>
        </div>
      </div>
      <div className="rounded-lg border border-white/10 bg-black/30 p-3">
        <div className="text-[10px] uppercase tracking-widest text-brand font-outfit font-bold mb-1">Client Feedback</div>
        <div className="flex items-center gap-1 text-gold-500 mb-1">
          {[1,2,3,4,5].map(i => <Star key={i} className="w-4 h-4 fill-current" />)}
          <span className="ml-2 text-white font-outfit font-bold">5.0</span>
        </div>
        <p className="text-sm text-white/85 font-manrope italic">
          "Excellent service, professional team and very well managed."
        </p>
        <div className="mt-1 text-[11px] text-white/50 font-manrope">— Operations Manager</div>
      </div>
      <button className="mt-3 text-xs text-brand font-outfit font-bold uppercase tracking-widest">View Client Feedback</button>
    </Card>
  );
}

function JobActionsCard({ job, isAdmin, isPoster, busy, onApprove, onDelete, statusDraft, setStatusDraft, notes, setNotes }) {
  const canDelete = isAdmin || isPoster;
  const canEdit   = isAdmin || isPoster;
  return (
    <Card title="Job Actions" testId="jd-actions">
      {canEdit && (
        <div className="space-y-3">
          <div>
            <label className="text-[10px] uppercase tracking-widest text-white/50 font-outfit font-bold">Job Status</label>
            <div className="mt-1 flex items-center gap-2">
              <span className="w-2 h-2 rounded-full bg-gold-500" />
              <select
                value={statusDraft}
                onChange={(e) => setStatusDraft(e.target.value)}
                data-testid="jd-status-select"
                className="flex-1 bg-black/40 border border-white/10 rounded-md px-3 py-2 text-sm text-white focus:outline-none focus:border-brand"
              >
                {["pending_review","open","filled","in_progress","completed","cancelled"].map(s => (
                  <option key={s} value={s}>{s.replace("_"," ").replace(/\b\w/g,c=>c.toUpperCase())}</option>
                ))}
              </select>
            </div>
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-widest text-white/50 font-outfit font-bold">Completion Notes (optional)</label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={2}
              placeholder="Great team performance. No issues reported."
              className="mt-1 w-full bg-black/40 border border-white/10 rounded-md px-3 py-2 text-sm text-white placeholder:text-white/40 font-manrope resize-none focus:outline-none focus:border-brand"
              data-testid="jd-notes"
            />
          </div>
        </div>
      )}
      <div className="mt-4 space-y-2">
        {isAdmin && job.status === "pending_review" && (
          <Button className="w-full bg-brand hover:bg-brand-hover text-black font-outfit font-bold uppercase tracking-widest text-xs" disabled={busy} onClick={onApprove} data-testid="jd-approve">
            <CheckCircle2 className="w-4 h-4 mr-1.5" /> Approve & open to pros
          </Button>
        )}
        {canEdit && (
          <Button className="w-full bg-gold-500 hover:bg-gold-500/90 text-black font-outfit font-bold uppercase tracking-widest text-xs" data-testid="jd-save-notes">
            Save
          </Button>
        )}
        {canDelete && (
          <Button variant="outline" className="w-full border-red-500/50 bg-red-500/10 hover:bg-red-500/20 text-red-400 font-outfit font-bold uppercase tracking-widest text-xs" disabled={busy} onClick={onDelete} data-testid="jd-delete">
            <Trash2 className="w-4 h-4 mr-1.5" /> Delete job
          </Button>
        )}
      </div>
    </Card>
  );
}

function ReportCard({ full }) {
  return (
    <Card title="Report" testId="jd-report">
      <p className="text-xs text-white/60 font-manrope mb-3">
        Download or generate a full audit-grade job report — overview, participants, timestamps, incidents and client feedback.
      </p>
      <Button className="w-full bg-brand hover:bg-brand-hover text-black font-outfit font-bold uppercase tracking-widest text-xs" data-testid="jd-report-generate">
        <FileText className="w-4 h-4 mr-1.5" /> Generate PDF Report
      </Button>
      {full && (
        <div className="mt-4 grid gap-2">
          <Button variant="outline" className="btn-ghost-dark text-xs uppercase tracking-widest"><Share2 className="w-4 h-4 mr-1.5" /> Share via Email</Button>
          <Button variant="outline" className="btn-ghost-dark text-xs uppercase tracking-widest"><Download className="w-4 h-4 mr-1.5" /> Download raw JSON</Button>
        </div>
      )}
    </Card>
  );
}

function TimelineCard({ job }) {
  const items = [
    { ts: job.created_at || job.start_at, title: "Job posted",             detail: "Awaiting admin review" },
    { ts: job.start_at,                   title: "Deployment started",      detail: "Team on-site" },
    { ts: job.end_at,                     title: "Deployment ended",        detail: "Team stood down" },
  ].filter(x => x.ts);
  return (
    <Card title="Timeline" testId="jd-timeline">
      {items.length === 0 && <div className="text-sm text-white/50 font-manrope italic">No timeline events yet.</div>}
      <ol className="relative border-l border-white/10 ml-3">
        {items.map((it, i) => (
          <li key={i} className="ml-4 pb-4 last:pb-0">
            <span className="absolute -left-[7px] w-3 h-3 rounded-full bg-brand border-2 border-[#141414]" />
            <div className="text-[10px] uppercase tracking-widest text-white/50 font-outfit font-bold">{fmtDate(it.ts)} · {fmtTimeRange(it.ts, null)}</div>
            <div className="text-sm text-white font-manrope">{it.title}</div>
            {it.detail && <div className="text-xs text-white/60 font-manrope">{it.detail}</div>}
          </li>
        ))}
      </ol>
    </Card>
  );
}
